import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { guestTokenHeaders } from '@lib/livePolls/guestToken';
import type {
  LiveAnswerBody,
  LiveAnswerRead,
  LiveClaimBody,
  LiveClaimRead,
  LiveJoinBody,
  LiveJoinRead,
  LiveMe,
  LiveOpenRead,
  QuestionLiveRead,
  UUID,
} from '@models/api';
import { customInstance } from './api-client';

// AIDEV-NOTE: the room's own live routes (TECH-867 FE-L3a; CONTRACT § 4.10, § 4.11, § 4.14). Through
// `customInstance` with its JSON default (CONTRACT § 11): a signed-in viewer's bearer rides along, and
// `X-Live-Guest-Token` is set per call on `/me`, `/open` and `/answer` only. The polled `/state` is a bare
// fetch (`liveReads.ts`). Kept in its own module, as `liveReads.ts` and `liveMoves.ts` are, so the live lane
// does not edit the shared `api-client.ts`.

const livePath = (code: string, route: string) => `/api/v1/live/${encodeURIComponent(code)}/${route}`;

/** `GET /live/{code}/me`: what this viewer may do and has answered. */
export const getLiveMe = (code: string, token: string | null): Promise<AxiosResponse<LiveMe>> =>
  customInstance<LiveMe>({ url: livePath(code, 'me'), method: 'GET', headers: guestTokenHeaders(token) });

/** `POST /live/{code}/open` (body `{}`): a new guest token when signed out with none, a one-time claim. */
export const openLive = (code: string, token: string | null = null): Promise<AxiosResponse<LiveOpenRead>> =>
  customInstance<LiveOpenRead>({
    url: livePath(code, 'open'),
    method: 'POST',
    data: {},
    headers: guestTokenHeaders(token),
  });

/** `PUT /live/{code}/answer`: the full set of picks on the question at `position` (`[]` takes it back). */
export const answerLive = (
  code: string,
  body: LiveAnswerBody,
  token: string | null,
): Promise<AxiosResponse<LiveAnswerRead>> =>
  customInstance<LiveAnswerRead>({
    url: livePath(code, 'answer'),
    method: 'PUT',
    data: body,
    headers: guestTokenHeaders(token),
  });

/**
 * `POST /live/{code}/join` (signed in): joins the join Space by its settings (`{}` open, `access_code` PIN,
 * `request_access` approval). `options` carries the bearer when sign-in has just finished (the replay).
 */
export const joinLive = (
  code: string,
  body: LiveJoinBody,
  options?: Pick<AxiosRequestConfig, 'headers'>,
): Promise<AxiosResponse<LiveJoinRead>> =>
  customInstance<LiveJoinRead>({ url: livePath(code, 'join'), method: 'POST', data: body, ...options });

/** `POST /live/{code}/claim` (signed in): redeems the guest's one-time claim (`LIVE_POLL_ANSWERS` replay). */
export const claimLive = (
  code: string,
  body: LiveClaimBody,
  options?: Pick<AxiosRequestConfig, 'headers'>,
): Promise<AxiosResponse<LiveClaimRead>> =>
  customInstance<LiveClaimRead>({ url: livePath(code, 'claim'), method: 'POST', data: body, ...options });

/** `GET /questions/{id}/live`: the feed card's read for a reader of the post (no guest token). */
export const getQuestionLive = (questionId: UUID, signal?: AbortSignal): Promise<AxiosResponse<QuestionLiveRead>> =>
  customInstance<QuestionLiveRead>({
    url: `/api/v1/questions/${encodeURIComponent(questionId)}/live`,
    method: 'GET',
    signal,
  });
